import { Avatar, Icon, IconNames, Tooltip } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classNames from 'classnames';
import { accessControl } from 'lib-shared-access-control';
import { Presence } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { Component } from 'react';

import { AwayStatus } from 'apis/presence';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { getMetaWorkingMedia } from 'utils/channels/media';
import { pubSub } from 'utils/pubSub';

import { numberFormatterInternational } from '../../../app/utils/telephonyHelpers';
import { buttonize } from '../../shared/util/buttonize';
import { ProfileProps } from '../containers/profile';

import MediaChannelBadges from './MediaChannelBadges';
import { PresenceDropdown } from './PresenceDropdown';
import styles from './profile.module.scss';

import './../styles/profile.less';

type PropsT = ProfileProps & {
  isSmall?: boolean;
  alignRight?: boolean;
  className?: string;
};

class Profile extends Component<PropsT> {
  @boundMethod
  changeYourStatus(location: 'topbar' | 'topbarDropdown'): void {
    if (this.props.presence.status === 'working') {
      this.props.presenceSetStatus({ status: 'away' }).then(() => {
        pubSub.publish('presenceAwayStatusSet');
        ampli.presenceUpdated({ status: 'away', location });
      });
    } else {
      this.props
        .presenceSetStatus({ status: 'working' })
        .then(() => ampli.presenceUpdated({ status: 'working', location }));
    }
  }

  @boundMethod
  toggleSimultaneousRing(location = 'topbar'): void {
    if (
      this.props.profile &&
      this.props.profile.phone_number &&
      typeof this.props.organization.trial_ended_at === 'string'
    ) {
      this.props
        .presenceSetSimultaneousRinging(
          !this.props.presence.presence_status.simultaneous_ringing,
        )
        .then((response) => {
          this.props.notificationRemoveByType('simring-on');
          if (response.data.enabled === true) {
            const phoneNumber = numberFormatterInternational({
              phoneNumber: this.props.profile.phone_number as string,
            });
            // Show notification that simultaneous ring is now enabled.
            const options = {
              type: 'simring-on',
              title: 'Simultaneous ring enabled',
              text: `You will now receive calls on the phone number ${phoneNumber}`,
              time: 10000,
            };
            this.props.notificationAdd(options);
          }

          ampli.simultaneousRingUpdated({
            enabled: response.data.enabled,
            location,
          });
        });
    }
  }

  getPresenceStatus(presence: Presence, numberOfAcw: number): string {
    if (presence.status !== 'offline' && numberOfAcw > 0) {
      return 'in wrap-up time';
    }

    if (['working', 'away', 'offline'].includes(presence.status)) {
      return presence.status;
    }

    return 'offline';
  }

  @boundMethod
  setCustomAwayStatus(
    awayStatus: Pick<AwayStatus, 'id' | 'label' | 'emoji'>,
  ): void {
    ampli.agentCustomAwayStatusUpdated({
      statusEmoji: awayStatus.emoji,
      statusLabel: awayStatus.label,
    });
    this.props.presenceSetStatus({
      status: 'away',
      custom_away_status_id: awayStatus.id,
    });
  }

  getOppositePresenceStatus(presence: Presence): string {
    switch (presence.status) {
      case 'working':
        return 'away';
      case 'away':
        return 'working';
      case 'offline':
      default:
        return 'online';
    }
  }

  getIconClass(presence: Presence, numberOfAcw: number): IconNames {
    if (presence.status !== 'offline' && numberOfAcw > 0) {
      return 'clock';
    }

    return 'working';
  }

  render() {
    const hasAccess = accessControl.hasAccess('presence.working_media');
    const { hasPhoneChannel, organization, presence, presenceMeta, profile } =
      this.props;
    const numberOfAcw =
      this.props.presence.presence_status.load_descriptions.filter(
        ({ load_type }) => load_type === 'acw',
      ).length;

    const isInTrial =
      typeof organization.trial_ended_at === 'string' ? false : true;

    let simultaneousRingingActiveBadge = ' away';
    let simultaneousRingingText =
      isInTrial === false
        ? 'Simultaneous ring is turned off'
        : 'Simultaneous is turned off because you are in trial.';
    const presenceStatus = this.getPresenceStatus(presence, numberOfAcw);
    const oppositePresenceStatus = this.getOppositePresenceStatus(presence);
    const iconClasses: IconNames = this.getIconClass(presence, numberOfAcw);
    let validPhoneNumber = false;

    const metaWorkingMedia = getMetaWorkingMedia(presence.working_channels);

    if (
      profile.phone_number !== null &&
      presence.presence_status.simultaneous_ringing === true
    ) {
      simultaneousRingingActiveBadge = ' active working';
      simultaneousRingingText = `Simultaneous ring is turned on ${numberFormatterInternational(
        { phoneNumber: profile.phone_number as string },
      )}`;
    } else {
      simultaneousRingingActiveBadge = ' disabled';
    }

    if (this.props.profile && this.props.profile.phone_number) {
      validPhoneNumber = true;
    }

    const presenceTooltipContent = (
      <CommandKeybindingsTooltipContent commandName="app:togglePresence">
        You are {presenceStatus}. Click to set {oppositePresenceStatus}
      </CommandKeybindingsTooltipContent>
    );

    return (
      <div
        className={classNames(`topbar-presence`, this.props.className)}
        tabIndex={0}
      >
        <span className={styles.avatarContainer}>
          {this.props.isSmall && (
            <span
              className={classNames(styles.avatarBadge, {
                [styles.avatarBadgeWorking]:
                  this.props.presence.status === 'working',
                [styles.avatarBadgeAway]: this.props.presence.status === 'away',
              })}
            />
          )}
          <Avatar user={profile} size="24" />
        </span>
        {!this.props.isSmall && (
          <div className="mystatus noselect">
            <span className="badge-item no-margin status">
              <Tooltip content={presenceTooltipContent} wrap="none">
                <span
                  {...buttonize(() => this.changeYourStatus('topbar'))}
                  className="profile-presence-icon-big"
                >
                  <Icon
                    name={iconClasses}
                    size={24}
                    className={`${this.props.presence.status} badge-btn ${
                      presenceMeta.setAsyncStatus === 'loading'
                        ? 'icon-animated'
                        : ''
                    }`}
                  />
                </span>
              </Tooltip>
            </span>

            <span className={`${this.props.presence.status} badges-icons`}>
              {metaWorkingMedia.map((metaMedia) => (
                <MediaChannelBadges
                  key={metaMedia.name}
                  presenceMeta={presenceMeta}
                  hasAccess={hasAccess}
                  telephony={this.props.telephony}
                  presence={this.props.presence}
                  presenceSetMedia={this.props.presenceSetMedia}
                  errorMicrophoneErrorShowModal={
                    this.props.errorMicrophoneErrorShowModal
                  }
                  personalToggleRememberMicrophoneAccess={
                    this.props.personalToggleRememberMicrophoneAccess
                  }
                  metaMedia={metaMedia}
                />
              ))}
            </span>

            {hasPhoneChannel && (
              <>
                <span className="simultaneous-ring badge-item no-margin">
                  <Tooltip
                    content={
                      hasAccess
                        ? simultaneousRingingText
                        : 'Restricted by organization'
                    }
                    wrap="none"
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        verticalAlign: 'middle',
                      }}
                      onClick={() => {
                        hasAccess &&
                        validPhoneNumber == true &&
                        isInTrial === false
                          ? this.toggleSimultaneousRing()
                          : null;
                      }}
                    >
                      <Icon
                        name="simultaneous"
                        size={24}
                        className={`${simultaneousRingingActiveBadge} profile-presence-icon-big badge-btn`}
                      />
                    </span>
                  </Tooltip>
                </span>
              </>
            )}
          </div>
        )}

        <PresenceDropdown
          presence={this.props.presence}
          profile={profile}
          metaWorkingMedia={metaWorkingMedia}
          telephony={this.props.telephony}
          setCustomAwayStatus={this.setCustomAwayStatus}
          presenceSetMedia={this.props.presenceSetMedia}
          presenceSetMediaChannel={this.props.presenceSetMediaChannel}
          errorMicrophoneErrorShowModal={
            this.props.errorMicrophoneErrorShowModal
          }
          personalToggleRememberMicrophoneAccess={
            this.props.personalToggleRememberMicrophoneAccess
          }
          validPhoneNumber={validPhoneNumber}
          isInTrial={isInTrial}
          isPresenceStatusLoading={presenceMeta.setAsyncStatus === 'loading'}
          toggleSimultaneousRing={this.toggleSimultaneousRing}
          hasAccess={hasAccess}
          hasPhoneChannel={hasPhoneChannel}
          changeYourStatus={this.changeYourStatus}
          oppositePresenceStatus={oppositePresenceStatus}
          showLogoutModal={this.props.showLogoutModal}
        />
      </div>
    );
  }
}

export default Profile;
